import {darken, lighten} from '@mui/material/styles'
import {UiTheme} from '@view/theme/types'
import {IAccountStyleTokens, MenuColor} from '@containers/accountStyle/types'
import {
	ACCOUNT_MENU_PALETTE,
	DEFAULT_ACCOUNT_MENU_COLOR,
} from '@containers/accountStyle/constants/palette'

const DARK_PANEL = '#1d1e22'
const HEX_COLOR = /^#[0-9a-f]{6}$/

const channels = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))

export const mix = (color: string, base: string, weight: number) => {
	const a = channels(color)
	const b = channels(base)
	return `rgb(${a.map((value, i) => Math.round(value * (1 - weight) + b[i] * weight)).join(', ')})`
}

// Same threshold as track's MenuColorStyle.
const isLight = (hex: string) => {
	const [r, g, b] = channels(hex)
	return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6
}

export const isCustomMenuColor = (value?: string | null): value is string =>
	!!value && HEX_COLOR.test(value.toLowerCase())

export const parseMenuColor = (value?: string | null): MenuColor | null => {
	if (!value) return null
	if (value in ACCOUNT_MENU_PALETTE) return value
	return isCustomMenuColor(value) ? value.toLowerCase() : null
}

export const resolveMenuColorHex = (value?: string | null): string => {
	const color = parseMenuColor(value) ?? DEFAULT_ACCOUNT_MENU_COLOR
	return isCustomMenuColor(color) ? color : ACCOUNT_MENU_PALETTE[color as keyof typeof ACCOUNT_MENU_PALETTE]
}

// The colours the panel gradient runs through; whatever sits on the panel has to read on each.
export const panelGradientStops = (color: string) => [lighten(color, 0.12), color, darken(color, 0.28)]

export const buildPanelGradient = (color: string) => {
	const [top, middle, bottom] = panelGradientStops(color)
	return `linear-gradient(165deg, ${top} 0%, ${middle} 45%, ${bottom} 100%)`
}

export const buildAccountStyleTokens = (menuColor: MenuColor, theme: UiTheme): IAccountStyleTokens => {
	const accent = resolveMenuColorHex(menuColor)

	if (theme === 'dark') {
		return {
			accent,
			panelBackground: `linear-gradient(165deg, #2a2b31 0%, ${DARK_PANEL} 45%, #131417 100%)`,
			headerBackground: 'linear-gradient(90deg, #1b1c20, #2a2b30)',
			text: '#fff',
			textMuted: 'rgba(255, 255, 255, .62)',
			hover: 'rgba(255, 255, 255, .07)',
			selected: mix(accent, DARK_PANEL, 0.7),
			divider: 'rgba(255, 255, 255, .06)',
			activeBar: lighten(accent, 0.12),
		}
	}

	const light = isLight(accent)

	return {
		accent,
		panelBackground: buildPanelGradient(accent),
		headerBackground: `linear-gradient(90deg, ${darken(accent, 0.34)}, ${darken(accent, 0.12)})`,
		text: light ? '#1f1f1f' : '#fff',
		textMuted: light ? 'rgba(0, 0, 0, .66)' : 'rgba(255, 255, 255, .78)',
		hover: light ? 'rgba(0, 0, 0, .07)' : 'rgba(255, 255, 255, .12)',
		selected: light ? 'rgba(0, 0, 0, .11)' : 'rgba(255, 255, 255, .2)',
		divider: light ? 'rgba(0, 0, 0, .08)' : 'rgba(255, 255, 255, .13)',
		activeBar: light ? '#1f1f1f' : '#fff',
	}
}
